import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useQueryGetActiveTestSessions from '@/api/user/getActiveTestSessions.ts'
import useQueryGetMyPermission, { PermissionAction, PermissionSubject } from '@/api/auth/getPermission.ts'
import ClockIcon from '@/assets/ic_clock.svg?react'
import ResponsivePopover from '@/component/testMode/ResponsivePopover.tsx'
import TestStatusPanel from '@/component/testMode/TestStatusPanel.tsx'
import useTestSessionActions from '@/component/testMode/useTestSessionActions.tsx'
import useCountdown from '@/util/hook/useCountdown.ts'
import { earliestEndsAt, formatRemaining, splitByOwner } from '@/util/testSession.ts'

const SHEET_MAX_WIDTH = 599

const TestModeBanner: React.FC = () => {
    const { t } = useTranslation()
    const { data: sessions = [] } = useQueryGetActiveTestSessions()
    const { hasPermission } = useQueryGetMyPermission()
    const { end, isBusy } = useTestSessionActions()
    const [isPanelOpen, setIsPanelOpen] = useState(false)
    const remaining = formatRemaining(useCountdown(earliestEndsAt(sessions)))

    useEffect(() => {
        if (sessions.length < 2) setIsPanelOpen(false)
    }, [sessions.length])

    if (sessions.length === 0) return null

    const canEdit = hasPermission(PermissionSubject.ALERT_EMERGENCY, PermissionAction.UPDATE)
    const { mine, others } = splitByOwner(sessions)
    const first = [...mine, ...others][0]
    const isSingle = sessions.length === 1

    const fullText = isSingle
        ? first.isMine
            ? t('testMode.banner.single', { name: first.protecteeName })
            : t('testMode.banner.singleOther', { name: first.protecteeName, owner: first.createdByName })
        : t('testMode.banner.multi', { count: sessions.length, name: first.protecteeName, rest: sessions.length - 1 })
    const shortText = isSingle
        ? t('testMode.banner.singleShort', { name: first.protecteeName })
        : t('testMode.banner.multiShort', { count: sessions.length, name: first.protecteeName, rest: sessions.length - 1 })
    const compactText = isSingle
        ? t('testMode.banner.singleCompact', { name: first.protecteeName, time: remaining })
        : t('testMode.banner.multiCompact', { count: sessions.length, time: remaining })
    const countdownText = isSingle
        ? t('testMode.remaining', { time: remaining })
        : t('testMode.banner.firstEnds', { time: remaining })

    return (
        <section className={'test-mode-banner'} aria-label={t('testMode.banner.ariaLabel')}>
            <div className={'test-mode-banner__inner'}>
                <ClockIcon className={'test-mode-banner__icon'} aria-hidden />
                <p className={'test-mode-banner__text'}>
                    <span className={'test-mode-banner__text--full'}>{fullText}</span>
                    <span className={'test-mode-banner__text--short'}>{shortText}</span>
                    <span className={'test-mode-banner__text--compact'}>{compactText}</span>
                </p>
                <span className={'test-mode-banner__countdown'}>{countdownText}</span>
                {isSingle && canEdit && (
                    <button
                        type={'button'}
                        className={'test-mode-button'}
                        disabled={isBusy(first.protecteeId)}
                        onClick={() => end(first)}>
                        <span className={'test-mode-banner__label--wide'}>{t('testMode.banner.end')}</span>
                        <span className={'test-mode-banner__label--narrow'}>{t('testMode.action.end')}</span>
                    </button>
                )}
                {!isSingle && (
                    <ResponsivePopover
                        isOpen={isPanelOpen}
                        onOpenChange={setIsPanelOpen}
                        placement={'bottom-end'}
                        sheetMaxWidth={SHEET_MAX_WIDTH}
                        ariaLabel={t('testMode.panel.title')}
                        surfaceClassName={'test-mode-popover--panel'}
                        renderTrigger={({ ref, props }) => (
                            <button
                                ref={ref}
                                type={'button'}
                                className={'test-mode-button'}
                                aria-expanded={isPanelOpen}
                                {...props}>
                                <span className={'test-mode-banner__label--wide'}>
                                    {isPanelOpen ? t('testMode.banner.statusClose') : t('testMode.banner.status')}
                                </span>
                                <span className={'test-mode-banner__label--narrow'}>{t('testMode.banner.statusShort')}</span>
                            </button>
                        )}>
                        <TestStatusPanel sessions={sessions} canEdit={canEdit} />
                    </ResponsivePopover>
                )}
            </div>
        </section>
    )
}

export default TestModeBanner
